import { useState, useEffect, useCallback } from 'react';

export interface MinimizedDrawerItem {
  id: string;
  title: string;
  data?: any;
}

export interface MinimizedDrawersState {
  minimizedDrawers: MinimizedDrawerItem[];
  minimizeDrawer: (id: string, title: string, data?: any) => void;
  restoreDrawer: (id: string) => void;
  isMinimized: (id: string) => boolean;
  lastRestoredItem: MinimizedDrawerItem | null;
  clearLastRestoredItem: () => void;
  clearMinimizedDrawer: (id: string) => void;
}

export function useMinimizedDrawersState(
  storageKey: string,
): MinimizedDrawersState & { isLoaded: boolean } {
  const [minimizedDrawers, setMinimizedDrawers] = useState<
    MinimizedDrawerItem[]
  >([]);
  const [lastRestoredItem, setLastRestoredItem] =
    useState<MinimizedDrawerItem | null>(null);
  const [isLoaded, setIsLoaded] = useState(false);

  useEffect(() => {
    try {
      const stored = sessionStorage.getItem(storageKey);
      if (stored) {
        const parsed = JSON.parse(stored);
        if (Array.isArray(parsed)) {
          setMinimizedDrawers(parsed);
        } else if (
          typeof parsed === 'object' &&
          parsed !== null &&
          Array.isArray(parsed.minimizedDrawers)
        ) {
          setMinimizedDrawers(parsed.minimizedDrawers);
        }
      }
    } catch (err) {
      // eslint-disable-next-line no-console
      console.error('Failed to load minimized drawers from sessionStorage:', err);
      sessionStorage.removeItem(storageKey);
    }
    setIsLoaded(true);
  // storageKey is constant per provider instance — intentional single-run
  // eslint-disable-next-line react-hooks/exhaustive-deps
  }, []);

  useEffect(() => {
    if (!isLoaded) return;
    try {
      sessionStorage.setItem(storageKey, JSON.stringify(minimizedDrawers));
    } catch (err) {
      // eslint-disable-next-line no-console
      console.error('Failed to save minimized drawers to sessionStorage:', err);
    }
  }, [minimizedDrawers, isLoaded, storageKey]);

  const minimizeDrawer = useCallback(
    (id: string, title: string, data?: any) => {
      setMinimizedDrawers(prev => {
        if (prev.find(d => d.id === id)) {
          return prev.map(d => (d.id === id ? { ...d, title, data } : d));
        }
        return [...prev, { id, title, data }];
      });
    },
    [],
  );

  const restoreDrawer = useCallback((id: string) => {
    let found: MinimizedDrawerItem | undefined;
    setMinimizedDrawers(prev => {
      found = prev.find(d => d.id === id);
      return prev.filter(d => d.id !== id);
    });
    setLastRestoredItem(found ?? null);
  }, []);

  const clearLastRestoredItem = useCallback(() => {
    setLastRestoredItem(null);
  }, []);

  const clearMinimizedDrawer = useCallback(
    (id: string) => {
      setMinimizedDrawers(prev => prev.filter(d => d.id !== id));
      setLastRestoredItem(prev => (prev?.id === id ? null : prev));
    },
    [],
  );

  const isMinimized = useCallback(
    (id: string) => minimizedDrawers.some(d => d.id === id),
    [minimizedDrawers],
  );

  return {
    isLoaded,
    minimizedDrawers,
    minimizeDrawer,
    restoreDrawer,
    isMinimized,
    lastRestoredItem,
    clearLastRestoredItem,
    clearMinimizedDrawer,
  };
}
